<template lang="pug">
	.carto-map-screener-root
		.carto-map-screener(ref="screener", :style="{ top: `-${height}px` }")
			.screener-map-holder(ref="holder", :style="{ width: `${width}px`, height: `${height}px` }")
				.screener-map(:style="{ width: `${width}px`, height: `${height}px` }")
</template>

<script>
	import $ from 'jquery';
	import VueTypes from 'vue-types';
	import dynamic from 'dynamic';
	import api from './api.js';
	import html2canvas from 'html2canvas';
	import Data from 'app-core/Data';

	export default {
		translationCategory: 'carto.CartoMapScreener',
		name: 'carto-map-screener',
		props: {
			source: VueTypes.any.def(null),
			width: VueTypes.number.def(1920),
			height: VueTypes.number.def(1920),
			mapPosition: VueTypes.object.def({}),
			cartoStyle: VueTypes.string.def(null),
			featureGroupItems: VueTypes.array.def(null)
		},
		data() {
			return {
				layer: null,
				leafletLayer: null,
				client: null,
				featureGroup: null
			};
		},
		watch: {
			source() {
				this.createLayers();
				this.generateScreen();
			},
			'source._query'() {
				this.createLayers();
				this.generateScreen();
			},
			featureGroupItems() {
				this.addFeatureGroupItems();
				this.generateScreen();
			}
		},
		mounted() {
			if (__DEBUG__)
				window.cartoMapScreener = this;
			window.jQuery = $;
			this.client = api.newClient();
			dynamic.loadOnce([
				'https://unpkg.com/leaflet@1.5.1/dist/leaflet.js',
				'https://unpkg.com/leaflet@1.5.1/dist/leaflet.css',
			]).then(() => {
				return dynamic.loadOnce([
					'https://libs.cartocdn.com/carto.js/v4.2.0/carto.min.js',
					'https://carto.com/developers/carto-js/examples/maps/public/style.css',
					'https://cdn.jsdelivr.net/npm/leaflet-draw@1.0.4/dist/leaflet.draw.js',
					'https://cdn.jsdelivr.net/npm/leaflet-draw@1.0.4/dist/leaflet.draw.css'
				]).then(() => {
					if (__DEBUG__)
						console.debug('Carto map screener loaded');
					this.buildMap();
					this.createLayers();
					if (this.source || (this.featureGroupItems && this.featureGroupItems.length)) {
						this.generateScreen();
					}
					return this.$map;
				});
			});
		},
		destroyed() {
			delete this.$map;
			if (this.$screener) {
				this.$el.appendChild(this.$screener);
				delete this.$screener;
			}
		},
		methods: {
			addFeatureGroupItems() {
				if (!this.featureGroup) return;
				this.featureGroup.clearLayers();
				if (this.featureGroupItems) {
					this.featureGroupItems.map(f => {
						f.addTo(this.featureGroup);
					});
				}
			},
			createLayers() {
				const carto = window.carto;

				if (!this.client || !this.$map) return;
				if (this.layer) this.client.removeLayer(this.layer);
				this.layer = null;

				const style = new carto.style.CartoCSS(this.cartoStyle || `
					#layer {
						polygon-fill: #ff8f59;
						polygon-opacity: 0.6;
					}
					#layer::outline {
						line-width: 0;
					}
				`);

				if (this.source) {
					this.layer = new carto.layer.Layer(this.source, style);
					this.client.addLayer(this.layer);
				}
			},
			buildMap() {
				if (__DEBUG__)
					console.log('Build map screener');

				const L = window.L;
				const map = this.$map = L.map($(this.$el).find('.screener-map')[0], {
					zoomControl: false
				}).setView([this.mapPosition.lat || 37.8, this.mapPosition.lng || -96], this.mapPosition.zoom || 5);

				L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/light_all/{z}/{x}/{y}@2x.png', {
					maxZoom: 18
				}).addTo(map);

				this.leafletLayer = this.client.getLeafletLayer();
				this.leafletLayer.addTo(this.$map);

				this.$map.getRenderer(this.$map).options.padding = 0;

				this.featureGroup = L.featureGroup().addTo(map);
				this.addFeatureGroupItems();

				this.$screener = this.$refs.screener;
				document.body.appendChild(this.$screener);
			},
			generateScreen() {
				let rendered = false;
				const loaded = () => {
					if (this._isDestroyed || rendered || this.layersTilesLoading()) return;
					rendered = true;
					this.getLayers().map(l => l.off('load', loaded));
					setTimeout(() => {
						if (this._isDestroyed) return;
						html2canvas(this.$refs.holder, {
							useCORS: true,
							allowTaint: true,
						}).then(canvas => {
							if (__DEBUG__)
								console.debug('Html2Canvas result', canvas);
							this.$emit('image-generated', canvas.toDataURL('image/jpeg', 0.95));
						});
					}, 1500);
				};

				const setBounds = (bounds) => {
					if (__DEBUG__)
						console.debug('Bounds changed', bounds);
					if (bounds.isValid && !bounds.isValid()) return;
					this.$emit('start-generation');
					this.$emit('bounds-changed', bounds);
					this.$map.fitBounds(bounds);
					setTimeout(() => {
						if (this._isDestroyed) return;
						if (!this.layersTilesLoading())
							loaded();
						else
							this.getLayers().map(l => l.on('load', loaded));
					}, 500);
				};

				if (!this.source)  {
					if (this.featureGroup) {
						const bounds = this.featureGroup.getBounds();
						if (!this.$map) return;
						setBounds(bounds);
					}
					return;
				};
				const query = `WITH bbox as (
						SELECT ST_Envelope(the_geom) as the_geom
						FROM (${this.source.getQuery()}) as data
					)
				SELECT MAX(ST_Xmax(the_geom)) as xmax,
						MAX(ST_Ymax(the_geom)) as ymax,
						MIN(ST_Xmin(the_geom)) as xmin,
						MIN(ST_Ymin(the_geom)) as ymin
				FROM bbox`;

				return Data.getCartoSql(query).then(r => {
					if (this._isDestroyed) return;
					if (__DEBUG__)
						console.debug('Bounding box loaded', { r });

					if (!this.layer || !this.$map) return;
					setBounds([ [r[0].ymin, r[0].xmin], [r[0].ymax, r[0].xmax] ]);
				});
			},
			layersTilesLoading() {
				return this.getLayers().some(f => f._noTilesToLoad && !f._noTilesToLoad());
			},
			getLayers() {
				return Object.keys(this.$map._layers).map(k => this.$map._layers[k]);
			}
		}
	};
</script>
<style lang='scss'>
	@import "main/themes/generated/variables.base.scss";

	.carto-map-screener {
		position: fixed;
	}
</style>
